
import React, { useState, useEffect, useMemo } from 'react';
import { createClient } from '@supabase/supabase-js';
import { 
  Home, Users, FolderOpen, MessageSquare, Calendar, BookOpen, Settings, 
  Bell, Search, Menu, X, Plus, Filter, Download, Upload, Edit, Trash2, 
  Clock, MapPin, Phone, Mail, Building, User, Star, Award, Target, 
  CheckCircle, AlertCircle, Info, HelpCircle, Heart, ThumbsUp, Share2,
  FileText, Image, Video, Archive, Paperclip, Eye, ExternalLink,
  Send, MoreVertical, ChevronDown, ChevronRight, ArrowRight, ArrowLeft,
  Zap, Globe, Shield, Rocket, TrendingUp, Activity, BarChart3, PieChart,
  Coffee, Wifi, Car, Utensils, Gift, Briefcase, GraduationCap, Headphones,
  Monitor, Smartphone, Laptop, Printer, Camera, Mic, Battery, Signal,
  Gauge, Timer, Play, Pause, Square, RefreshCw, Save, LogOut, Lock,
  Key, CreditCard, Package, Truck, Plane, Train, Bus, Navigation,
  Sun, Moon, Cloud, Umbrella, Thermometer, Wind, Droplets, Factory,
  Lightbulb, Vote, TrendingDown, AlertTriangle, CheckSquare, XSquare, 
  Bookmark, DollarSign, Receipt, CalendarDays, ShoppingCart, BookMinus,
  Loader, CircleDot, UserCheck, UserX, CalendarCheck, CalendarX,
  FileCheck, FilePlus, TrendingUp as TrendUp, BarChart, Database,
  Percent, Target as TargetIcon, Wrench, HardHat, ChevronLeft, CircleCheck,
  Layers, FileSpreadsheet, FileMinus, FolderPlus, UserPlus
} from 'lucide-react';

// Initialize Supabase client
const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL || '',
  process.env.REACT_APP_SUPABASE_ANON_KEY || ''
);

const GaloIntranet = () => {
  const [currentView, setCurrentView] = useState('home');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [showForm, setShowForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);

  // Data states
  const [projects, setProjects] = useState([]);
  const [people, setPeople] = useState([]);
  const [siteAssignments, setSiteAssignments] = useState([]);
  const [travelRequests, setTravelRequests] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [news, setNews] = useState([]);

  // Auth states
  const [isSignUp, setIsSignUp] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);

  // Status update modal
  const [showStatusModal, setShowStatusModal] = useState(false);

  // WORKING AUTH CHECK (keep this simple!)
  useEffect(() => {
    console.log('=== AUTH EFFECT RUNNING ===');
    
    checkAuth();
    
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('=== AUTH CHANGE ===', event, session?.user?.email);
      
      if (session?.user?.email?.endsWith('@galo-industries.com')) {
        console.log('Valid user - setting');
        setUser(session.user);
        // Load profile and data in background after user is set
        loadUserData(session.user);
        setLoading(false);
      } else if (session?.user) {
        console.log('Invalid domain');
        setError('Access restricted to @galo-industries.com email addresses');
        setUser(null);
        setLoading(false);
      } else {
        console.log('No session');
        setUser(null);
        setUserProfile(null);
        clearAllData();
        setLoading(false);
      }
    });

    return () => {
      console.log('=== CLEANING UP AUTH ===');
      subscription.unsubscribe();
    };
  }, []);

  const checkAuth = async () => {
    try {
      console.log('=== CHECKING INITIAL SESSION ===');
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error) {
        console.error('Session error:', error);
        setError(error.message);
        setLoading(false);
        return;
      }

      console.log('Session found:', session?.user?.email);

      if (session?.user?.email?.endsWith('@galo-industries.com')) {
        console.log('Setting user from initial session');
        setUser(session.user);
        // Load profile and data in background
        loadUserData(session.user);
      } else if (session?.user) {
        console.log('Invalid domain in initial session');
        setError('Access restricted to @galo-industries.com email addresses');
        await supabase.auth.signOut();
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Auth check error:', err);
      setLoading(false);
    }
  };

  // Load user profile and data in background
  const loadUserData = async (user) => {
    console.log('Loading user data for:', user.email);
    
    // Load profile first
    try {
      await loadUserProfile(user.id);
    } catch (error) {
      console.error('Profile loading failed:', error);
      // Set basic profile as fallback
      setUserProfile({
        id: user.id,
        name: user.email.split('@')[0].replace('.', ' '),
        email: user.email,
        role: 'Employee',
        active: true,
        location_status: 'hq'
      });
    }

    // Load all data in background
    loadAllData();
  };

  const loadUserProfile = async (userId) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();
      
      if (error) {
        if (error.code === 'PGRST116') {
          console.log('Profile not found, using default');
        } else {
          console.error('Profile error:', error);
        }
        // Use default profile
        setUserProfile({
          id: userId,
          name: user?.email?.split('@')[0].replace('.', ' ') || 'User',
          email: user?.email || '',
          role: 'Employee',
          active: true,
          location_status: 'hq'
        });
      } else if (!data.active) {
        setError('Your account has been deactivated. Please contact administration.');
        await supabase.auth.signOut();
      } else {
        setUserProfile(data);
        setError(null);
      }
    } catch (error) {
      console.error('Error loading profile:', error);
      // Set fallback profile
      setUserProfile({
        id: userId,
        name: user?.email?.split('@')[0].replace('.', ' ') || 'User',
        email: user?.email || '',
        role: 'Employee',
        active: true,
        location_status: 'hq'
      });
    }
  };

  const clearAllData = () => {
    setProjects([]);
    setPeople([]);
    setSiteAssignments([]);
    setTravelRequests([]);
    setExpenses([]);
    setLeaveRequests([]);
    setNews([]);
  };

  // Data loading functions
  const loadAllData = async () => {
    console.log('Loading all data...');
    try {
      // Load data in parallel but don't block the UI
      Promise.all([
        loadProjects(),
        loadPeople(),
        loadSiteAssignments(),
        loadTravelRequests(),
        loadExpenses(),
        loadLeaveRequests(),
        loadNews()
      ]).catch(error => {
        console.error('Some data failed to load:', error);
        // Don't show error to user unless critical
      });
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const loadProjects = async () => {
    try {
      const { data, error } = await supabase
        .from('projects')
        .select(`
          *,
          project_manager:profiles!projects_pm_id_fkey(name, email),
          lead_engineer:profiles!projects_lead_engineer_id_fkey(name, email)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setProjects(data || []);
    } catch (error) {
      console.error('Error loading projects:', error);
    }
  };

  const loadPeople = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('active', true)
        .order('name');

      if (error) throw error;
      setPeople(data || []);
    } catch (error) {
      console.error('Error loading people:', error);
    }
  };

  const loadSiteAssignments = async () => {
    try {
      const { data, error } = await supabase
        .from('site_assignments')
        .select(`
          *,
          person:profiles!site_assignments_person_id_fkey(name, email),
          project:projects(project_id, client, location)
        `)
        .order('start_date', { ascending: false });

      if (error) throw error;
      setSiteAssignments(data || []);
    } catch (error) {
      console.error('Error loading site assignments:', error);
    }
  };

  const loadTravelRequests = async () => {
    try {
      const { data, error } = await supabase
        .from('travel_requests')
        .select(`
          *,
          requester:profiles!travel_requests_requester_id_fkey(name, email),
          approver:profiles!travel_requests_approver_id_fkey(name, email),
          project:projects(project_id, client)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setTravelRequests(data || []);
    } catch (error) {
      console.error('Error loading travel requests:', error);
    }
  };

  const loadExpenses = async () => {
    try {
      const { data, error } = await supabase
        .from('expenses')
        .select(`
          *,
          employee:profiles!expenses_employee_id_fkey(name, email),
          approver:profiles!expenses_approver_id_fkey(name, email),
          project:projects(project_id, client)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setExpenses(data || []);
    } catch (error) {
      console.error('Error loading expenses:', error);
    }
  };

  const loadLeaveRequests = async () => {
    try {
      const { data, error } = await supabase
        .from('leave_requests')
        .select(`
          *,
          employee:profiles!leave_requests_employee_id_fkey(name, email),
          approver:profiles!leave_requests_approver_id_fkey(name, email)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setLeaveRequests(data || []);
    } catch (error) {
      console.error('Error loading leave requests:', error);
    }
  };

  const loadNews = async () => {
    try {
      const { data, error } = await supabase
        .from('news')
        .select(`
          *,
          author:profiles!news_author_id_fkey(name, email),
          project:projects(project_id, client)
        `)
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) throw error;
      setNews(data || []);
    } catch (error) {
      console.error('Error loading news:', error);
    }
  };

  // Helper functions
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric'
    });
  };

  const timeAgo = (dateString) => {
    if (!dateString) return 'Unknown';
    const diff = Date.now() - new Date(dateString).getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);
    
    if (hours < 1) return 'Just now';
    if (hours < 24) return `${hours}h ago`;
    if (days === 1) return 'Yesterday';
    return `${days} days ago`;
  };

  // Form submission functions
  const submitTravelRequest = async (formData) => {
    setSubmitLoading(true);
    setError(null);
    
    try {
      const project = projects.find(p => p.id === formData.projectId);
      
      const { error } = await supabase
        .from('travel_requests')
        .insert({
          requester_id: user.id,
          project_id: formData.projectId || null,
          approver_id: project?.pm_id || null,
          destination: formData.destination,
          departure_date: formData.departureDate,
          return_date: formData.returnDate,
          purpose: formData.purpose,
          status: 'requested'
        });

      if (error) throw error;
      
      setShowForm(null);
      setSuccess('Travel request submitted successfully!');
      loadTravelRequests();
      
    } catch (error) {
      console.error('Error submitting travel request:', error);
      setError(error.message);
    } finally {
      setSubmitLoading(false);
    }
  };

  const submitExpense = async (formData) => {
    setSubmitLoading(true);
    setError(null);
    
    try {
      let receiptUrl = null;
      
      if (formData.receipt) {
        const fileExt = formData.receipt.name.split('.').pop();
        const fileName = `${user.id}-${Date.now()}.${fileExt}`;
        
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('receipts')
          .upload(fileName, formData.receipt);

        if (uploadError) {
          console.warn('Receipt upload failed:', uploadError);
        } else {
          receiptUrl = uploadData.path;
        }
      }

      const project = projects.find(p => p.id === formData.projectId);

      const { error } = await supabase
        .from('expenses')
        .insert({
          employee_id: user.id,
          project_id: formData.projectId || null,
          approver_id: project?.pm_id || null,
          amount: parseFloat(formData.amount),
          category: formData.category,
          description: formData.description,
          receipt_url: receiptUrl,
          status: 'submitted',
          date: new Date().toISOString().split('T')[0]
        });

      if (error) throw error;
      
      setShowForm(null);
      setSuccess('Expense submitted successfully!');
      loadExpenses();
      
    } catch (error) {
      console.error('Error submitting expense:', error);
      setError(error.message);
    } finally {
      setSubmitLoading(false);
    }
  };

  const submitLeaveRequest = async (formData) => {
    setSubmitLoading(true);
    setError(null);
    
    try {
      const startDate = new Date(formData.startDate);
      const endDate = new Date(formData.endDate);
      const days = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
      
      const { error } = await supabase
        .from('leave_requests')
        .insert({
          employee_id: user.id,
          start_date: formData.startDate,
          end_date: formData.endDate,
          type: formData.type,
          days: days,
          reason: formData.reason || null,
          status: 'requested'
        });

      if (error) throw error;
      
      setShowForm(null);
      setSuccess('Leave request submitted successfully!');
      loadLeaveRequests();
      
    } catch (error) {
      console.error('Error submitting leave request:', error);
      setError(error.message);
    } finally {
      setSubmitLoading(false);
    }
  };

  // Update user status/location
  const updateUserStatus = async (status, location = null) => {
    try {
      const updates = {
        id: user.id,
        location_status: status,
        updated_at: new Date().toISOString()
      };

      if (location) {
        updates.current_location = location;
      }

      const { error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', user.id);

      if (error) throw error;

      setUserProfile(prev => ({ ...prev, ...updates }));
      setSuccess('Status updated successfully!');
      setShowStatusModal(false);
      
      // Reload people to update directory
      loadPeople();
    } catch (error) {
      console.error('Error updating status:', error);
      setError('Failed to update status');
    }
  };

  // Simple auth functions
  const handleAuth = async (email, password) => {
    if (!email.endsWith('@galo-industries.com')) {
      setError('Only @galo-industries.com email addresses are allowed');
      return;
    }

    setAuthLoading(true);
    setError(null);

    try {
      if (isSignUp) {
        console.log('Signing up user:', email);
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: {
              name: email.split('@')[0].replace('.', ' ')
            }
          }
        });
        if (error) throw error;
        if (data.user && !data.user.email_confirmed_at) {
          setSuccess('Please check your email and click the confirmation link.');
        }
      } else {
        console.log('Signing in user:', email);
        const { data, error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        if (error) throw error;
      }
    } catch (error) {
      console.error('Auth error:', error);
      setError(error.message);
    } finally {
      setAuthLoading(false);
    }
  };

  const signOut = async () => {
    console.log('Signing out...');
    const { error } = await supabase.auth.signOut();
    if (error) {
      console.error('Sign out error:', error);
      setError(error.message);
    }
  };

  // ============= STATUS UPDATE MODAL =============
  const StatusUpdateModal = () => {
    const [status, setStatus] = useState(userProfile?.location_status || 'hq');
    const [customLocation, setCustomLocation] = useState('');
    const [selectedProject, setSelectedProject] = useState('');

    const handleStatusUpdate = () => {
      let location = null;
      if (status === 'on_site') {
        if (selectedProject) {
          const project = projects.find(p => p.id === selectedProject);
          location = project?.location || customLocation;
        } else {
          location = customLocation;
        }
      } else if (status === 'remote') {
        location = 'Remote';
      } else {
        location = 'HQ';
      }

      updateUserStatus(status, location);
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-2xl p-6 w-full max-w-md mx-4">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-slate-900">Update My Location</h2>
            <button 
              onClick={() => setShowStatusModal(false)} 
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Where are you working?
              </label>
              <div className="grid grid-cols-3 gap-3">
                <button
                  onClick={() => setStatus('hq')}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    status === 'hq' 
                      ? 'border-blue-500 bg-blue-50 text-blue-700' 
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <Building className="w-6 h-6 mx-auto mb-1" />
                  <span className="text-sm font-medium">HQ</span>
                </button>
                <button
                  onClick={() => setStatus('on_site')}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    status === 'on_site' 
                      ? 'border-green-500 bg-green-50 text-green-700' 
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <MapPin className="w-6 h-6 mx-auto mb-1" />
                  <span className="text-sm font-medium">On Site</span>
                </button>
                <button
                  onClick={() => setStatus('remote')}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    status === 'remote' 
                      ? 'border-purple-500 bg-purple-50 text-purple-700' 
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <Wifi className="w-6 h-6 mx-auto mb-1" />
                  <span className="text-sm font-medium">Remote</span>
                </button>
              </div>
            </div>

            {status === 'on_site' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Project Site (optional)
                  </label>
                  <select 
                    className="w-full p-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-green-500"
                    value={selectedProject}
                    onChange={(e) => setSelectedProject(e.target.value)}
                  >
                    <option value="">Select project...</option>
                    {projects.filter(p => p.status === 'active').map(project => (
                      <option key={project.id} value={project.id}>
                        {project.project_id} - {project.client} ({project.location})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Or Enter Location
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., Client Office, Training Center"
                    className="w-full p-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-green-500"
                    value={customLocation}
                    onChange={(e) => setCustomLocation(e.target.value)}
                  />
                </div>
              </>
            )}

            <button
              onClick={handleStatusUpdate}
              className="w-full bg-blue-600 text-white py-3 rounded-xl font-medium hover:bg-blue-700 transition-colors"
            >
              Update Status
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ============= ALL COMPONENT DEFINITIONS INSIDE MAIN COMPONENT =============
  
  // Form Components
  const TravelRequestForm = () => {
    const [formData, setFormData] = useState({
      projectId: '',
      destination: '',
      departureDate: '',
      returnDate: '',
      purpose: ''
    });

    const [formErrors, setFormErrors] = useState({});

    const validateForm = () => {
      const errors = {};
      
      if (!formData.destination.trim()) errors.destination = 'Destination is required';
      if (!formData.departureDate) errors.departureDate = 'Departure date is required';
      if (!formData.returnDate) errors.returnDate = 'Return date is required';
      if (!formData.purpose.trim()) errors.purpose = 'Purpose is required';
      
      if (formData.departureDate && formData.returnDate) {
        if (new Date(formData.departureDate) >= new Date(formData.returnDate)) {
          errors.returnDate = 'Return date must be after departure date';
        }
      }
      
      setFormErrors(errors);
      return Object.keys(errors).length === 0;
    };

    const handleSubmit = () => {
      if (validateForm()) {
        submitTravelRequest(formData);
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-2xl p-6 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-slate-900">Request Travel</h2>
            <button 
              onClick={() => setShowForm(null)} 
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Project (optional)
              </label>
              <select 
                className="w-full p-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500"
                value={formData.projectId}
                onChange={(e) => setFormData({...formData, projectId: e.target.value})}
              >
                <option value="">Select project...</option>
                {projects.map(project => (
                  <option key={project.id} value={project.id}>
                    {project.project_id} - {project.client}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Destination *
              </label>
              <input
                type="text"
                placeholder="City or site location"
                className={`w-full p-3 border rounded-xl focus:ring-2 focus:ring-blue-500 ${
                  formErrors.destination ? 'border-red-300' : 'border-slate-300'
                }`}
                value={formData.destination}
                onChange={(e) => setFormData({...formData, destination: e.target.value})}
              />
              {formErrors.destination && (
                <p className="text-red-600 text-xs mt-1">{formErrors.destination}</p>
              )}
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Departure *
                </label>
                <input
                  type="date"
                  className={`w-full p-3 border rounded-xl focus:ring-2 focus:ring-blue-500 ${
                    formErrors.departureDate ? 'border-red-300' : 'border-slate-300'
                  }`}
                  value={formData.departureDate}
                  min={new Date().toISOString().split('T')[0]}
                  onChange={(e) => setFormData({...formData, departureDate: e.target.value})}
                />
                {formErrors.departureDate && (
                  <p className="text-red-600 text-xs mt-1">{formErrors.departureDate}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Return *
                </label>
                <input
                  type="date"
                  className={`w-full p-3 border rounded-xl focus:ring-2 focus:ring-blue-500 ${
                    formErrors.returnDate ? 'border-red-300' : 'border-slate-300'
                  }`}
                  value={formData.returnDate}
                  min={formData.departureDate || new Date().toISOString().split('T')[0]}
                  onChange={(e) => setFormData({...formData, returnDate: e.target.value})}
                />
                {formErrors.returnDate && (
                  <p className="text-red-600 text-xs mt-1">{formErrors.returnDate}</p>
                )}
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Purpose *
              </label>
              <textarea
                rows="3"
                placeholder="Brief description of travel purpose"
                className={`w-full p-3 border rounded-xl focus:ring-2 focus:ring-blue-500 resize-none ${
                  formErrors.purpose ? 'border-red-300' : 'border-slate-300'
                }`}
                value={formData.purpose}
                onChange={(e) => setFormData({...formData, purpose: e.target.value})}
              />
              {formErrors.purpose && (
                <p className="text-red-600 text-xs mt-1">{formErrors.purpose}</p>
              )}
            </div>
            
            <button
              onClick={handleSubmit}
              disabled={submitLoading}
              className="w-full bg-blue-600 text-white py-3 rounded-xl font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {submitLoading ? (
                <>
                  <Loader className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit Request'
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const ExpenseForm = () => {
    const [formData, setFormData] = useState({
      projectId: '',
      amount: '',
      category: 'Meals',
      description: '',
      receipt: null
    });

    const [formErrors, setFormErrors] = useState({});

    const validateForm = () => {
      const errors = {};
      
      if (!formData.amount || parseFloat(formData.amount) <= 0) {
        errors.amount = 'Valid amount is required';
      }
      if (!formData.description.trim()) errors.description = 'Description is required';
      
      setFormErrors(errors);
      return Object.keys(errors).length === 0;
    };

    const handleSubmit = () => {
      if (validateForm()) {
        submitExpense(formData);
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-2xl p-6 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-slate-900">Submit Expense</h2>
            <button 
              onClick={() => setShowForm(null)} 
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Project (optional)
              </label>
              <select 
                className="w-full p-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-green-500"
                value={formData.projectId}
                onChange={(e) => setFormData({...formData, projectId: e.target.value})}
              >
                <option value="">Select project...</option>
                {projects.map(project => (
                  <option key={project.id} value={project.id}>
                    {project.project_id} - {project.client}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Amount (EUR) *
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  className={`w-full p-3 border rounded-xl focus:ring-2 focus:ring-green-500 ${
                    formErrors.amount ? 'border-red-300' : 'border-slate-300'
                  }`}
                  value={formData.amount}
                  onChange={(e) => setFormData({...formData, amount: e.target.value})}
                />
                {formErrors.amount && (
                  <p className="text-red-600 text-xs mt-1">{formErrors.amount}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Category
                </label>
                <select 
                  className="w-full p-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-green-500"
                  value={formData.category}
                  onChange={(e) => setFormData({...formData, category: e.target.value})}
                >
                  <option value="Travel">Travel</option>
                  <option value="Tools">Tools</option>
                  <option value="Meals">Meals</option>
                  <option value="Misc">Misc</option>
                </select>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Description *
              </label>
              <input
                type="text"
                placeholder="Brief description"
                className={`w-full p-3 border rounded-xl focus:ring-2 focus:ring-green-500 ${
                  formErrors.description ? 'border-red-300' : 'border-slate-300'
                }`}
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
              />
              {formErrors.description && (
                <p className="text-red-600 text-xs mt-1">{formErrors.description}</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Receipt
              </label>
              <input
                type="file"
                accept="image/*,.pdf"
                className="w-full p-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-green-500"
                onChange={(e) => setFormData({...formData, receipt: e.target.files[0]})}
              />
              <p className="text-xs text-slate-500 mt-1">Upload photo or PDF of receipt (optional)</p>
            </div>
            
            <button
              onClick={handleSubmit}
              disabled={submitLoading}
              className="w-full bg-green-600 text-white py-3 rounded-xl font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {submitLoading ? (
                <>
                  <Loader className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit Expense'
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const LeaveRequestForm = () => {
    const [formData, setFormData] = useState({
      startDate: '',
      endDate: '',
      type: 'Vacation',
      reason: ''
    });

    const [formErrors, setFormErrors] = useState({});

    const validateForm = () => {
      const errors = {};
      
      if (!formData.startDate) errors.startDate = 'Start date is required';
      if (!formData.endDate) errors.endDate = 'End date is required';
      
      if (formData.startDate && formData.endDate) {
        if (new Date(formData.startDate) > new Date(formData.endDate)) {
          errors.endDate = 'End date must be after start date';
        }
      }
      
      setFormErrors(errors);
      return Object.keys(errors).length === 0;
    };

    const handleSubmit = () => {
      if (validateForm()) {
        submitLeaveRequest(formData);
      }
    };

    const calculateDays = () => {
      if (formData.startDate && formData.endDate) {
        const start = new Date(formData.startDate);
        const end = new Date(formData.endDate);
        const days = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
        return days > 0 ? days : 0;
      }
      return 0;
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-2xl p-6 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-slate-900">Request Leave</h2>
            <button 
              onClick={() => setShowForm(null)} 
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Start Date *
                </label>
                <input
                  type="date"
                  className={`w-full p-3 border rounded-xl focus:ring-2 focus:ring-yellow-500 ${
                    formErrors.startDate ? 'border-red-300' : 'border-slate-300'
                  }`}
                  value={formData.startDate}
                  min={new Date().toISOString().split('T')[0]}
                  onChange={(e) => setFormData({...formData, startDate: e.target.value})}
                />
                {formErrors.startDate && (
                  <p className="text-red-600 text-xs mt-1">{formErrors.startDate}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  End Date *
                </label>
                <input
                  type="date"
                  className={`w-full p-3 border rounded-xl focus:ring-2 focus:ring-yellow-500 ${
                    formErrors.endDate ? 'border-red-300' : 'border-slate-300'
                  }`}
                  value={formData.endDate}
                  min={formData.startDate || new Date().toISOString().split('T')[0]}
                  onChange={(e) => setFormData({...formData, endDate: e.target.value})}
                />
                {formErrors.endDate && (
                  <p className="text-red-600 text-xs mt-1">{formErrors.endDate}</p>
                )}
              </div>
            </div>

            {calculateDays() > 0 && (
              <div className="text-sm text-slate-600 bg-slate-50 p-2 rounded">
                Total days: {calculateDays()}
              </div>
            )}
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Type
              </label>
              <select 
                className="w-full p-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-yellow-500"
                value={formData.type}
                onChange={(e) => setFormData({...formData, type: e.target.value})}
              >
                <option value="Vacation">Vacation</option>
                <option value="Sick Leave">Sick Leave</option>
                <option value="Personal">Personal</option>
                <option value="Emergency">Emergency</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Reason (optional)
              </label>
              <textarea
                rows="3"
                placeholder="Brief description of leave reason"
                className="w-full p-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-yellow-500 resize-none"
                value={formData.reason}
                onChange={(e) => setFormData({...formData, reason: e.target.value})}
              />
            </div>
            
            <button
              onClick={handleSubmit}
              disabled={submitLoading}
              className="w-full bg-yellow-600 text-white py-3 rounded-xl font-medium hover:bg-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {submitLoading ? (
                <>
                  <Loader className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit Request'
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Auth form component
  const AuthForm = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');

    const handleSubmit = (e) => {
      e.preventDefault();
      handleAuth(email, password);
    };

    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-600 via-cyan-600 to-blue-700 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl p-8 w-full max-w-md shadow-2xl">
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-3xl flex items-center justify-center mx-auto mb-6">
              <Factory className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-slate-900 mb-2">Galo Industries</h1>
            <p className="text-slate-600">Employee Intranet Portal</p>
          </div>
          
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <div className="text-red-800 text-sm">{error}</div>
            </div>
          )}

          {success && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <div className="text-green-800 text-sm">{success}</div>
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <input
                type="email"
                placeholder="your.name@galo-industries.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full p-4 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>
            
            <div>
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full p-4 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
                minLength="6"
              />
            </div>
            
            <button
              type="submit"
              disabled={authLoading}
              className="w-full bg-blue-600 text-white py-4 px-6 rounded-xl font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {authLoading ? (
                <>
                  <Loader className="w-5 h-5 mr-2 animate-spin" />
                  {isSignUp ? 'Creating Account...' : 'Signing In...'}
                </>
              ) : (
                isSignUp ? 'Create Account' : 'Sign In'
              )}
            </button>
            
            <div className="text-center">
              <button
                type="button"
                onClick={() => {
                  setIsSignUp(!isSignUp);
                  setError(null);
                  setSuccess(null);
                }}
                className="text-blue-600 hover:text-blue-700 text-sm"
                disabled={authLoading}
              >
                {isSignUp ? 'Already have an account? Sign In' : 'Need an account? Sign Up'}
              </button>
            </div>
          </form>
          
          <p className="text-xs text-slate-500 text-center mt-6">
            Access is restricted to @galo-industries.com email addresses only
          </p>
        </div>
      </div>
    );
  };

  // COMPLETE HOME PAGE with data
  const HomePage = () => {
    const todayAssignments = siteAssignments.filter(assignment => {
      const today = new Date().toISOString().split('T')[0];
      return assignment.start_date <= today && 
             assignment.end_date >= today && 
             assignment.status === 'on_site';
    });

    const myRequests = {
      travel: travelRequests.filter(r => r.requester_id === user?.id),
      expenses: expenses.filter(e => e.employee_id === user?.id),
      leave: leaveRequests.filter(l => l.employee_id === user?.id)
    };

    const getLocationStatusDisplay = () => {
      if (userProfile?.location_status === 'on_site') {
        return {
          icon: <MapPin className="w-5 h-5" />,
          text: userProfile?.current_location || 'On Site',
          color: 'text-green-600 bg-green-100'
        };
      } else if (userProfile?.location_status === 'remote') {
        return {
          icon: <Wifi className="w-5 h-5" />,
          text: 'Working Remote',
          color: 'text-purple-600 bg-purple-100'
        };
      } else {
        return {
          icon: <Building className="w-5 h-5" />,
          text: 'At HQ',
          color: 'text-blue-600 bg-blue-100'
        };
      }
    };

    const locationStatus = getLocationStatusDisplay();

    return (
      <div className="space-y-8">
        <div className="bg-gradient-to-br from-blue-600 via-cyan-600 to-blue-700 rounded-3xl p-8 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold mb-2">
                Welcome back, {userProfile?.name?.split(' ')[0] || user?.email?.split('@')[0]}!
              </h1>
              <p className="text-blue-100 text-lg">Your operations command center</p>
              <button 
                onClick={() => setShowStatusModal(true)}
                className={`mt-4 px-4 py-2 rounded-lg flex items-center space-x-2 ${locationStatus.color} bg-white transition-colors hover:opacity-90`}
              >
                {locationStatus.icon}
                <span className="font-medium">{locationStatus.text}</span>
                <Edit className="w-4 h-4" />
              </button>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold">{new Date().getDate()}</div>
              <div className="text-blue-100">
                {new Date().toLocaleDateString('en-GB', { weekday: 'long', month: 'long' })}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
          <h2 className="text-xl font-bold text-slate-900 mb-6">Quick Actions</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {[
              { id: 'travel', name: 'Request Travel', icon: <Plane className="w-6 h-6" />, color: 'blue' },
              { id: 'expense', name: 'Submit Expense', icon: <Receipt className="w-6 h-6" />, color: 'green' },
              { id: 'leave', name: 'Request Leave', icon: <Sun className="w-6 h-6" />, color: 'yellow' }
            ].map(action => (
              <button
                key={action.id}
                onClick={() => setShowForm(action.id)}
                className={`p-4 rounded-xl border border-slate-200 hover:shadow-md transition-all duration-200 group ${
                  action.color === 'blue' ? 'bg-blue-50 hover:bg-blue-100' :
                  action.color === 'green' ? 'bg-green-50 hover:bg-green-100' :
                  'bg-yellow-50 hover:bg-yellow-100'
                }`}
              >
                <div className={`w-12 h-12 ${
                  action.color === 'blue' ? 'bg-blue-100 group-hover:bg-blue-200 text-blue-600' :
                  action.color === 'green' ? 'bg-green-100 group-hover:bg-green-200 text-green-600' :
                  'bg-yellow-100 group-hover:bg-yellow-200 text-yellow-600'
                } rounded-lg flex items-center justify-center mb-3 mx-auto transition-colors`}>
                  {action.icon}
                </div>
                <span className="text-sm font-medium text-slate-700 group-hover:text-slate-900 text-center block">
                  {action.name}
                </span>
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
              <h2 className="text-xl font-bold text-slate-900 mb-6">Who's On Site Today</h2>
              {todayAssignments.length === 0 ? (
                <div className="text-center py-8">
                  <MapPin className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No one is on client sites today</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {todayAssignments.map(assignment => (
                    <div key={assignment.id} className="flex items-center justify-between p-4 bg-green-50 rounded-lg border border-green-200">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                          {assignment.person?.name?.split(' ').map(n => n[0]).join('') || 'UK'}
                        </div>
                        <div>
                          <div className="font-medium text-slate-900">{assignment.person?.name || 'Unknown'}</div>
                          <div className="text-sm text-slate-600">{assignment.location}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-medium text-green-700">{assignment.role_on_site}</div>
                        <div className="text-xs text-slate-500">{assignment.project?.project_id || 'No project'}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
              <h2 className="text-xl font-bold text-slate-900 mb-6">Latest News</h2>
              {news.length === 0 ? (
                <div className="text-center py-8">
                  <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No recent news updates</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {news.slice(0, 3).map(item => (
                    <div key={item.id} className="border border-slate-200 rounded-xl p-4 hover:bg-slate-50 transition-colors">
                      <div className="flex items-start space-x-3">
                        <div className="w-10 h-10 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                          {item.author?.name?.split(' ').map(n => n[0]).join('') || 'UK'}
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-slate-900 mb-1">{item.title}</h3>
                          <p className="text-slate-600 text-sm mb-2">{item.body}</p>
                          <div className="flex items-center justify-between text-xs text-slate-500">
                            <span>by {item.author?.name || 'Unknown'} • {timeAgo(item.created_at)}</span>
                            {item.project && (
                              <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full">
                                {item.project.project_id}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
              <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center">
                <User className="w-5 h-5 mr-2" />
                My Requests
              </h2>
              
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 mb-2 flex items-center">
                    <Plane className="w-4 h-4 mr-2" />
                    Travel ({myRequests.travel.length})
                  </h3>
                  {myRequests.travel.length === 0 ? (
                    <p className="text-slate-500 text-sm">No travel requests</p>
                  ) : (
                    <div className="space-y-2">
                      {myRequests.travel.slice(0, 3).map(travel => (
                        <div key={travel.id} className="text-sm p-3 bg-slate-50 rounded-lg">
                          <div className="font-medium">{travel.destination}</div>
                          <div className="flex items-center justify-between">
                            <span className="text-slate-600">{formatDate(travel.departure_date)}</span>
                            <span className={`px-2 py-1 rounded text-xs ${
                              travel.status === 'approved' ? 'bg-green-100 text-green-700' :
                              travel.status === 'requested' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-slate-100 text-slate-700'
                            }`}>
                              {travel.status}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <h3 className="text-sm font-semibold text-slate-700 mb-2 flex items-center">
                    <Receipt className="w-4 h-4 mr-2" />
                    Expenses ({myRequests.expenses.length})
                  </h3>
                  {myRequests.expenses.length === 0 ? (
                    <p className="text-slate-500 text-sm">No expenses</p>
                  ) : (
                    <div className="space-y-2">
                      {myRequests.expenses.slice(0, 3).map(expense => (
                        <div key={expense.id} className="text-sm p-3 bg-slate-50 rounded-lg">
                          <div className="flex items-center justify-between">
                            <span className="font-medium">€{expense.amount}</span>
                            <span className={`px-2 py-1 rounded text-xs ${
                              expense.status === 'approved' ? 'bg-green-100 text-green-700' :
                              expense.status === 'submitted' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-slate-100 text-slate-700'
                            }`}>
                              {expense.status}
                            </span>
                          </div>
                          <div className="text-slate-600">{expense.category}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <h3 className="text-sm font-semibold text-slate-700 mb-2 flex items-center">
                    <Sun className="w-4 h-4 mr-2" />
                    Leave ({myRequests.leave.length})
                  </h3>
                  {myRequests.leave.length === 0 ? (
                    <p className="text-slate-500 text-sm">No leave requests</p>
                  ) : (
                    <div className="space-y-2">
                      {myRequests.leave.slice(0, 3).map(leave => (
                        <div key={leave.id} className="text-sm p-3 bg-slate-50 rounded-lg">
                          <div className="flex items-center justify-between">
                            <span className="font-medium">{leave.days} days</span>
                            <span className={`px-2 py-1 rounded text-xs ${
                              leave.status === 'approved' ? 'bg-green-100 text-green-700' :
                              leave.status === 'requested' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-slate-100 text-slate-700'
                            }`}>
                              {leave.status}
                            </span>
                          </div>
                          <div className="text-slate-600">{formatDate(leave.start_date)}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ============= MY REQUESTS DASHBOARD COMPONENT =============
  const MyRequestsDashboard = () => {
    const [activeTab, setActiveTab] = useState('all');
    
    const myRequests = {
      travel: travelRequests.filter(r => r.requester_id === user?.id),
      expenses: expenses.filter(e => e.employee_id === user?.id),
      leave: leaveRequests.filter(l => l.employee_id === user?.id)
    };

    const allRequests = [
      ...myRequests.travel.map(r => ({ ...r, type: 'travel', date: r.departure_date })),
      ...myRequests.expenses.map(e => ({ ...e, type: 'expense', date: e.date })),
      ...myRequests.leave.map(l => ({ ...l, type: 'leave', date: l.start_date }))
    ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

    const getStatusColor = (status) => {
      switch(status) {
        case 'approved': return 'bg-green-100 text-green-700';
        case 'rejected': return 'bg-red-100 text-red-700';
        case 'requested':
        case 'submitted': return 'bg-yellow-100 text-yellow-700';
        default: return 'bg-gray-100 text-gray-700';
      }
    };

    const getRequestIcon = (type) => {
      switch(type) {
        case 'travel': return <Plane className="w-5 h-5" />;
        case 'expense': return <Receipt className="w-5 h-5" />;
        case 'leave': return <Sun className="w-5 h-5" />;
        default: return <FileText className="w-5 h-5" />;
      }
    };

    const formatCurrency = (amount) => {
      return new Intl.NumberFormat('en-IE', {
        style: 'currency',
        currency: 'EUR'
      }).format(amount);
    };

    const filteredRequests = activeTab === 'all' ? allRequests : 
      allRequests.filter(r => r.type === activeTab);

    return (
      <div className="space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl p-6 text-white">
            <div className="flex items-center justify-between mb-4">
              <Plane className="w-8 h-8 opacity-80" />
              <span className="text-3xl font-bold">{myRequests.travel.length}</span>
            </div>
            <h3 className="text-lg font-semibold mb-1">Travel Requests</h3>
            <div className="text-sm opacity-90">
              {myRequests.travel.filter(r => r.status === 'approved').length} approved, 
              {' '}{myRequests.travel.filter(r => r.status === 'requested').length} pending
            </div>
          </div>

          <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-2xl p-6 text-white">
            <div className="flex items-center justify-between mb-4">
              <Receipt className="w-8 h-8 opacity-80" />
              <span className="text-3xl font-bold">
                {formatCurrency(myRequests.expenses.reduce((sum, e) => sum + parseFloat(e.amount || 0), 0))}
              </span>
            </div>
            <h3 className="text-lg font-semibold mb-1">Total Expenses</h3>
            <div className="text-sm opacity-90">
              {myRequests.expenses.length} submissions, 
              {' '}{myRequests.expenses.filter(e => e.status === 'approved').length} approved
            </div>
          </div>

          <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 rounded-2xl p-6 text-white">
            <div className="flex items-center justify-between mb-4">
              <Sun className="w-8 h-8 opacity-80" />
              <span className="text-3xl font-bold">
                {myRequests.leave.reduce((sum, l) => sum + l.days, 0)}
              </span>
            </div>
            <h3 className="text-lg font-semibold mb-1">Leave Days</h3>
            <div className="text-sm opacity-90">
              {myRequests.leave.filter(l => l.status === 'approved').length} approved, 
              {' '}{myRequests.leave.filter(l => l.status === 'requested').length} pending
            </div>
          </div>
        </div>

        {/* Tabs and List */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200">
          <div className="border-b border-slate-200">
            <div className="flex space-x-1 p-1">
              {['all', 'travel', 'expense', 'leave'].map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`flex-1 px-4 py-3 rounded-lg font-medium transition-colors ${
                    activeTab === tab 
                      ? 'bg-blue-100 text-blue-700' 
                      : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                  }`}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                  {tab !== 'all' && (
                    <span className="ml-2 text-sm">
                      ({tab === 'travel' ? myRequests.travel.length : 
                        tab === 'expense' ? myRequests.expenses.length : 
                        myRequests.leave.length})
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          <div className="p-6">
            {filteredRequests.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No requests found</p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredRequests.map(request => (
                  <div key={`${request.type}-${request.id}`} className="border border-slate-200 rounded-xl p-4 hover:bg-slate-50 transition-colors">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-4">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          request.type === 'travel' ? 'bg-blue-100 text-blue-600' :
                          request.type === 'expense' ? 'bg-green-100 text-green-600' :
                          'bg-yellow-100 text-yellow-600'
                        }`}>
                          {getRequestIcon(request.type)}
                        </div>
                        <div>
                          <h4 className="font-semibold text-slate-900">
                            {request.type === 'travel' ? `Travel to ${request.destination}` :
                             request.type === 'expense' ? `${request.category}: ${request.description}` :
                             `${request.type} Request - ${request.days} days`}
                          </h4>
                          <div className="text-sm text-slate-600 mt-1">
                            {request.type === 'travel' && (
                              <span>{new Date(request.departure_date).toLocaleDateString()} - {new Date(request.return_date).toLocaleDateString()}</span>
                            )}
                            {request.type === 'expense' && (
                              <span>{formatCurrency(request.amount)} • {new Date(request.date).toLocaleDateString()}</span>
                            )}
                            {request.type === 'leave' && (
                              <span>{new Date(request.start_date).toLocaleDateString()} - {new Date(request.end_date).toLocaleDateString()}</span>
                            )}
                          </div>
                          {request.project && (
                            <div className="text-xs text-slate-500 mt-1">
                              Project: {request.project.project_id} - {request.project.client}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col items-end space-y-2">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(request.status)}`}>
                          {request.status}
                        </span>
                        <span className="text-xs text-slate-500">
                          {new Date(request.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ============= OPERATIONS DASHBOARD COMPONENT =============
  const OperationsDashboard = () => {
    const [selectedMetric, setSelectedMetric] = useState('overview');

    // Calculate metrics
    const activeProjects = projects.filter(p => p.status === 'active').length;
    const totalPeopleOnSite = siteAssignments.filter(a => {
      const today = new Date().toISOString().split('T')[0];
      return a.start_date <= today && a.end_date >= today && a.status === 'on_site';
    }).length;
    
    const utilizationRate = people.length > 0 ? ((totalPeopleOnSite / people.length) * 100).toFixed(1) : 0;

    // Get projects by status
    const projectsByStatus = {
      active: projects.filter(p => p.status === 'active'),
      completed: projects.filter(p => p.status === 'completed'),
      on_hold: projects.filter(p => p.status === 'on_hold')
    };

    // Mock KPI data
    const kpiData = {
      safety: { score: 98, trend: 'up', incidents: 0 },
      quality: { score: 94, trend: 'stable', defects: 2 },
      schedule: { score: 87, trend: 'down', delayed: 1 },
      budget: { score: 91, trend: 'up', overruns: 0 }
    };

    return (
      <div className="space-y-6">
        {/* Main Dashboard */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
            <h2 className="text-xl font-bold text-slate-900 mb-6">Project Status Overview</h2>
            
            {/* Project Status Chart (simplified) */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="text-center p-4 bg-green-50 rounded-xl">
                <div className="text-3xl font-bold text-green-600">{projectsByStatus.active.length}</div>
                <div className="text-sm text-slate-600 mt-1">Active</div>
              </div>
              <div className="text-center p-4 bg-blue-50 rounded-xl">
                <div className="text-3xl font-bold text-blue-600">{projectsByStatus.completed.length}</div>
                <div className="text-sm text-slate-600 mt-1">Completed</div>
              </div>
              <div className="text-center p-4 bg-yellow-50 rounded-xl">
                <div className="text-3xl font-bold text-yellow-600">{projectsByStatus.on_hold.length}</div>
                <div className="text-sm text-slate-600 mt-1">On Hold</div>
              </div>
            </div>

            {/* Active Projects List */}
            <h3 className="font-semibold text-slate-900 mb-3">Active Projects</h3>
            <div className="space-y-3">
              {projectsByStatus.active.slice(0, 5).map(project => (
                <div key={project.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div>
                    <div className="font-medium text-slate-900">{project.project_id}</div>
                    <div className="text-sm text-slate-600">{project.client} • {project.location}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-medium text-slate-900">
                      {project.project_manager?.name || 'Unassigned'}
                    </div>
                    <div className="text-xs text-slate-500">Project Manager</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-6">
            {/* Resource Utilization */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
              <h3 className="text-lg font-bold text-slate-900 mb-4">Resource Utilization</h3>
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-slate-600">Field Staff</span>
                    <span className="font-medium">{utilizationRate}%</span>
                  </div>
                  <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full"
                      style={{ width: `${utilizationRate}%` }}
                    />
                  </div>
                </div>
                <div className="pt-2 border-t border-slate-200">
                  <div className="text-sm space-y-2">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Total Staff</span>
                      <span className="font-medium">{people.length}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">On Site</span>
                      <span className="font-medium text-green-600">{totalPeopleOnSite}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Available</span>
                      <span className="font-medium">{people.length - totalPeopleOnSite}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
              <h3 className="text-lg font-bold text-slate-900 mb-4">Quick Actions</h3>
              <div className="space-y-2">
                <button className="w-full text-left p-3 hover:bg-slate-50 rounded-lg transition-colors flex items-center justify-between group">
                  <span className="text-sm font-medium text-slate-700">Generate Weekly Report</span>
                  <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-slate-600" />
                </button>
                <button className="w-full text-left p-3 hover:bg-slate-50 rounded-lg transition-colors flex items-center justify-between group">
                  <span className="text-sm font-medium text-slate-700">View Resource Planning</span>
                  <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-slate-600" />
                </button>
                <button className="w-full text-left p-3 hover:bg-slate-50 rounded-lg transition-colors flex items-center justify-between group">
                  <span className="text-sm font-medium text-slate-700">Schedule Review Meeting</span>
                  <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-slate-600" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ============= DOCUMENTS LIBRARY COMPONENT =============
  const DocumentsLibrary = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [sortBy, setSortBy] = useState('date');

    // Mock documents data
    const documents = [
      { id: 1, name: 'Employee Handbook 2024.pdf', category: 'HR', size: '2.4 MB', uploadedBy: 'HR Department', uploadedAt: '2024-01-15', type: 'pdf' },
      { id: 2, name: 'Safety Procedures Manual.pdf', category: 'Safety', size: '5.1 MB', uploadedBy: 'Safety Team', uploadedAt: '2024-01-10', type: 'pdf' },
      { id: 3, name: 'Project GAL-2024-001 Specs.docx', category: 'Projects', size: '1.2 MB', uploadedBy: 'John Smith', uploadedAt: '2024-01-20', type: 'doc' },
      { id: 5, name: 'Training Video - Equipment.mp4', category: 'Training', size: '124 MB', uploadedBy: 'Training Team', uploadedAt: '2024-01-18', type: 'video' },
      { id: 6, name: 'Company Policies 2024.pdf', category: 'HR', size: '3.7 MB', uploadedBy: 'HR Department', uploadedAt: '2024-01-05', type: 'pdf' },
      { id: 7, name: 'Site Photos - GAL-2024-002.zip', category: 'Projects', size: '45.2 MB', uploadedBy: 'Field Team', uploadedAt: '2024-01-22', type: 'zip' },
      { id: 8, name: 'ISO Certification.pdf', category: 'Compliance', size: '1.8 MB', uploadedBy: 'Quality Team', uploadedAt: '2024-01-12', type: 'pdf' },
      { id: 9, name: 'Emergency Procedures.pdf', category: 'Safety', size: '890 KB', uploadedBy: 'Safety Team', uploadedAt: '2024-01-08', type: 'pdf' }    ];

    const categories = ['all', 'HR', 'Safety', 'Projects', 'Finance', 'Training', 'Compliance'];

    const getFileIcon = (type) => {
      switch(type) {
        case 'pdf': return <FileText className="w-8 h-8 text-red-500" />;
        case 'doc': return <FileText className="w-8 h-8 text-blue-500" />;
        case 'xls': return <FileSpreadsheet className="w-8 h-8 text-green-500" />;
        case 'video': return <Video className="w-8 h-8 text-purple-500" />;
        case 'zip': return <Archive className="w-8 h-8 text-yellow-600" />;
        default: return <FileText className="w-8 h-8 text-slate-400" />;
      }
    };

    const filteredDocuments = documents.filter(doc => {
      const matchesSearch = doc.name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = selectedCategory === 'all' || doc.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });

    const sortedDocuments = [...filteredDocuments].sort((a, b) => {
      if (sortBy === 'date') {
        return new Date(b.uploadedAt) - new Date(a.uploadedAt);
      } else if (sortBy === 'name') {
        return a.name.localeCompare(b.name);
      } else if (sortBy === 'size') {
        return parseFloat(b.size) - parseFloat(a.size);
      }
      return 0;
    });

    return (
      <div className="space-y-6">
        {/* Header and Controls */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-center space-x-4 flex-1">
              <div className="relative flex-1 max-w-md">
                <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search documents..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 w-full border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <select 
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500"
              >
                {categories.map(cat => (
                  <option key={cat} value={cat}>
                    {cat === 'all' ? 'All Categories' : cat}
                  </option>
                ))}
              </select>

              <select 
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500"
              >
                <option value="date">Latest First</option>
                <option value="name">Name (A-Z)</option>
                <option value="size">Size</option>
              </select>
            </div>

            <button className="px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors flex items-center">
              <Upload className="w-4 h-4 mr-2" />
              Upload Document
            </button>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-4 gap-4 mt-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-slate-900">{documents.length}</div>
              <div className="text-sm text-slate-600">Total Documents</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{categories.length - 1}</div>
              <div className="text-sm text-slate-600">Categories</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {documents.filter(d => new Date(d.uploadedAt) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)).length}
              </div>
              <div className="text-sm text-slate-600">This Week</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-slate-600">342 MB</div>
              <div className="text-sm text-slate-600">Total Size</div>
            </div>
          </div>
        </div>

        {/* Documents Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {sortedDocuments.map(doc => (
            <div key={doc.id} className="bg-white rounded-xl p-6 shadow-sm border border-slate-200 hover:shadow-md transition-all group">
              <div className="flex flex-col items-center text-center">
                <div className="mb-4">
                  {getFileIcon(doc.type)}
                </div>
                <h3 className="font-medium text-slate-900 mb-2 line-clamp-2">{doc.name}</h3>
                <span className="px-2 py-1 bg-slate-100 text-slate-600 rounded text-xs mb-3">
                  {doc.category}
                </span>
                <div className="text-sm text-slate-500 space-y-1">
                  <div>{doc.size}</div>
                  <div>by {doc.uploadedBy}</div>
                  <div>{new Date(doc.uploadedAt).toLocaleDateString()}</div>
                </div>
                <div className="flex items-center space-x-2 mt-4 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg">
                    <Eye className="w-4 h-4" />
                  </button>
                  <button className="p-2 text-green-600 hover:bg-green-50 rounded-lg">
                    <Download className="w-4 h-4" />
                  </button>
                  <button className="p-2 text-slate-600 hover:bg-slate-50 rounded-lg">
                    <Share2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // ============= REPORTS CENTER COMPONENT =============
  const ReportsCenter = () => {
    const [selectedReport, setSelectedReport] = useState('summary');
    const [dateRange, setDateRange] = useState('month');

    // Calculate summary metrics
    const totalExpenses = expenses.reduce((sum, e) => sum + parseFloat(e.amount || 0), 0);
    const approvedExpenses = expenses.filter(e => e.status === 'approved').reduce((sum, e) => sum + parseFloat(e.amount || 0), 0);
    const totalTravel = travelRequests.length;
    const approvedTravel = travelRequests.filter(t => t.status === 'approved').length;

    const reportTypes = [
      { id: 'summary', name: 'Executive Summary', icon: <BarChart3 className="w-5 h-5" /> },
      { id: 'financial', name: 'Financial Report', icon: <DollarSign className="w-5 h-5" /> },
      { id: 'projects', name: 'Project Status', icon: <FolderOpen className="w-5 h-5" /> },
      { id: 'resources', name: 'Resource Utilization', icon: <Users className="w-5 h-5" /> },
      { id: 'expenses', name: 'Expense Analysis', icon: <Receipt className="w-5 h-5" /> },
      { id: 'travel', name: 'Travel Summary', icon: <Plane className="w-5 h-5" /> }
    ];

    const formatCurrency = (amount) => {
      return new Intl.NumberFormat('en-IE', {
        style: 'currency',
        currency: 'EUR'
      }).format(amount);
    };

    return (
      <div className="space-y-6">
        {/* Report Selection */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-slate-900">Reports Center</h2>
            <div className="flex items-center space-x-3">
              <select 
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                className="px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500"
              >
                <option value="week">This Week</option>
                <option value="month">This Month</option>
                <option value="quarter">This Quarter</option>
                <option value="year">This Year</option>
              </select>
              <button className="px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors flex items-center">
                <Download className="w-4 h-4 mr-2" />
                Export PDF
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {reportTypes.map(report => (
              <button
                key={report.id}
                onClick={() => setSelectedReport(report.id)}
                className={`p-4 rounded-xl border transition-all ${
                  selectedReport === report.id 
                    ? 'bg-blue-50 border-blue-300 text-blue-700' 
                    : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                }`}
              >
                <div className="flex flex-col items-center space-y-2">
                  {report.icon}
                  <span className="text-xs font-medium text-center">{report.name}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Report Content */}
        {selectedReport === 'summary' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
              <h3 className="text-lg font-bold text-slate-900 mb-4">Key Metrics</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <span className="text-slate-600">Active Projects</span>
                  <span className="text-xl font-bold text-slate-900">{projects.filter(p => p.status === 'active').length}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <span className="text-slate-600">Total Expenses</span>
                  <span className="text-xl font-bold text-slate-900">{formatCurrency(totalExpenses)}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <span className="text-slate-600">People on Site</span>
                  <span className="text-xl font-bold text-slate-900">
                    {siteAssignments.filter(a => {
                      const today = new Date().toISOString().split('T')[0];
                      return a.start_date <= today && a.end_date >= today;
                    }).length}
                  </span>
                </div>
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <span className="text-slate-600">Travel Requests</span>
                  <span className="text-xl font-bold text-slate-900">{totalTravel}</span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
              <h3 className="text-lg font-bold text-slate-900 mb-4">Approval Rates</h3>
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-slate-600">Expense Approvals</span>
                    <span className="font-medium">{totalExpenses > 0 ? ((approvedExpenses / totalExpenses) * 100).toFixed(0) : 0}%</span>
                  </div>
                  <div className="h-3 bg-slate-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-green-500 rounded-full"
                      style={{ width: totalExpenses > 0 ? `${(approvedExpenses / totalExpenses) * 100}%` : '0%' }}
                    />
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-slate-600">Travel Approvals</span>
                    <span className="font-medium">{totalTravel > 0 ? ((approvedTravel / totalTravel) * 100).toFixed(0) : 0}%</span>
                  </div>
                  <div className="h-3 bg-slate-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-blue-500 rounded-full"
                      style={{ width: totalTravel > 0 ? `${(approvedTravel / totalTravel) * 100}%` : '0%' }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {selectedReport === 'expenses' && (
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
            <h3 className="text-lg font-bold text-slate-900 mb-6">Expense Breakdown by Category</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {['Travel', 'Meals', 'Tools', 'Misc'].map(category => {
                const categoryExpenses = expenses.filter(e => e.category === category);
                const total = categoryExpenses.reduce((sum, e) => sum + parseFloat(e.amount || 0), 0);
                
                return (
                  <div key={category} className="p-4 bg-slate-50 rounded-xl">
                    <div className="text-2xl font-bold text-slate-900 mb-1">{formatCurrency(total)}</div>
                    <div className="text-sm text-slate-600">{category}</div>
                    <div className="text-xs text-slate-500 mt-1">{categoryExpenses.length} transactions</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Generate Report Button */}
        <div className="bg-gradient-to-r from-slate-100 to-slate-200 rounded-2xl p-6 text-center">
          <h3 className="text-lg font-semibold text-slate-900 mb-2">Need a Custom Report?</h3>
          <p className="text-slate-600 mb-4">Generate detailed reports with custom parameters and filters</p>
          <button className="px-6 py-3 bg-slate-900 text-white rounded-xl hover:bg-slate-800 transition-colors">
            Create Custom Report
          </button>
        </div>
      </div>
    );
  };

  // ============= COMPANY NEWS COMPONENT =============
  const CompanyNews = () => {
    const [showNewPost, setShowNewPost] = useState(false);
    const [newPost, setNewPost] = useState({ title: '', body: '' });
    const [filter, setFilter] = useState('all');

    const filteredNews = filter === 'all' ? news : 
      filter === 'project' ? news.filter(n => n.project_id) :
      news.filter(n => !n.project_id);

    const handlePost = async () => {
      // In production, this would save to Supabase
      console.log('Posting:', newPost);
      setShowNewPost(false);
      setNewPost({ title: '', body: '' });
    };

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl p-8 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold mb-2">Company News & Updates</h1>
              <p className="text-purple-100">Stay informed about what's happening at Galo Industries</p>
            </div>
            {(userProfile?.role === 'Manager' || userProfile?.role === 'Admin') && (
              <button 
                onClick={() => setShowNewPost(true)}
                className="px-4 py-2 bg-white text-purple-600 rounded-xl hover:bg-purple-50 transition-colors flex items-center"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Post
              </button>
            )}
          </div>
        </div>

        {/* New Post Form */}
        {showNewPost && (
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
            <h3 className="text-lg font-bold text-slate-900 mb-4">Create News Post</h3>
            <div className="space-y-4">
              <input
                type="text"
                placeholder="Post title..."
                value={newPost.title}
                onChange={(e) => setNewPost({...newPost, title: e.target.value})}
                className="w-full p-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-purple-500"
              />
              <textarea
                rows="4"
                placeholder="What's the news?"
                value={newPost.body}
                onChange={(e) => setNewPost({...newPost, body: e.target.value})}
                className="w-full p-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-purple-500 resize-none"
              />
              <div className="flex justify-end space-x-3">
                <button 
                  onClick={() => setShowNewPost(false)}
                  className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-xl transition-colors"
                >
                  Cancel
                </button>
                <button 
                  onClick={handlePost}
                  className="px-4 py-2 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-colors"
                >
                  Post
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Filter Tabs */}
        <div className="bg-white rounded-xl p-1 shadow-sm border border-slate-200 inline-flex">
          {['all', 'general', 'project'].map(tab => (
            <button
              key={tab}
              onClick={() => setFilter(tab)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filter === tab 
                  ? 'bg-purple-100 text-purple-700' 
                  : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
              }`}
            >
              {tab === 'all' ? 'All News' : tab === 'general' ? 'General' : 'Project Updates'}
            </button>
          ))}
        </div>

        {/* News Feed */}
        <div className="space-y-4">
          {filteredNews.length === 0 ? (
            <div className="bg-white rounded-2xl p-12 shadow-sm border border-slate-200 text-center">
              <MessageSquare className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 text-lg">No news posts yet</p>
            </div>
          ) : (
            filteredNews.map(item => (
              <div key={item.id} className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200 hover:shadow-md transition-shadow">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white font-bold">
                    {item.author?.name?.split(' ').map(n => n[0]).join('') || '??'}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-slate-900 mb-2">{item.title}</h3>
                    <p className="text-slate-600 mb-4 whitespace-pre-wrap">{item.body}</p>
                    
                    {item.project && (
                      <div className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-700 rounded-lg text-sm mb-4">
                        <FolderOpen className="w-4 h-4 mr-2" />
                        {item.project.project_id} - {item.project.client}
                      </div>
                    )}
                    
                    <div className="flex items-center justify-between text-sm text-slate-500">
                      <div className="flex items-center space-x-4">
                        <span>{item.author?.name || 'Unknown'}</span>
                        <span>•</span>
                        <span>{new Date(item.created_at).toLocaleDateString('en-GB', { 
                          day: 'numeric', 
                          month: 'long', 
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
                          <ThumbsUp className="w-4 h-4" />
                        </button>
                        <button className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
                          <MessageSquare className="w-4 h-4" />
                        </button>
                        <button className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
                          <Share2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    );
  };

  // ============= SETTINGS COMPONENT =============
  const SettingsPage = () => {
    const [activeTab, setActiveTab] = useState('profile');

    return (
      <div className="space-y-6">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200">
          <div className="flex border-b border-slate-200">
            {['profile', 'notifications', 'preferences', 'security'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`flex-1 px-6 py-4 font-medium transition-colors ${
                  activeTab === tab 
                    ? 'text-blue-600 border-b-2 border-blue-600' 
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>

          <div className="p-6">
            {activeTab === 'profile' && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-slate-900 mb-4">Profile Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Name</label>
                    <input
                      type="text"
                      value={userProfile?.name || ''}
                      className="w-full p-3 border border-slate-300 rounded-xl"
                      readOnly
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                    <input
                      type="email"
                      value={userProfile?.email || ''}
                      className="w-full p-3 border border-slate-300 rounded-xl"
                      readOnly
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Department</label>
                    <input
                      type="text"
                      value={userProfile?.department || 'Not Set'}
                      className="w-full p-3 border border-slate-300 rounded-xl"
                      readOnly
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Role</label>
                    <input
                      type="text"
                      value={userProfile?.role || 'Employee'}
                      className="w-full p-3 border border-slate-300 rounded-xl"
                      readOnly
                    />
                  </div>
                </div>
                <button className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors">
                  Update Profile
                </button>
              </div>
            )}

            {activeTab === 'notifications' && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-slate-900 mb-4">Notification Preferences</h3>
                {['Email notifications', 'Push notifications', 'Weekly digest', 'Project updates'].map(setting => (
                  <label key={setting} className="flex items-center justify-between p-3 hover:bg-slate-50 rounded-lg">
                    <span className="text-slate-700">{setting}</span>
                    <input type="checkbox" className="w-5 h-5 text-blue-600 rounded" defaultChecked />
                  </label>
                ))}
              </div>
            )}

            {activeTab === 'preferences' && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-slate-900 mb-4">Display Preferences</h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Theme</label>
                    <select className="w-full p-3 border border-slate-300 rounded-xl">
                      <option>Light</option>
                      <option>Dark</option>
                      <option>System</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Language</label>
                    <select className="w-full p-3 border border-slate-300 rounded-xl">
                      <option>English</option>
                      <option>Spanish</option>
                      <option>French</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'security' && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-slate-900 mb-4">Security Settings</h3>
                <button className="w-full text-left p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-slate-900">Change Password</div>
                      <div className="text-sm text-slate-600">Update your password regularly</div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-400" />
                  </div>
                </button>
                <button className="w-full text-left p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-slate-900">Two-Factor Authentication</div>
                      <div className="text-sm text-slate-600">Add an extra layer of security</div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-400" />
                  </div>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ============= PEOPLE DIRECTORY (Your existing one) =============
  const PeopleDirectory = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [filterDepartment, setFilterDepartment] = useState('all');
    const [viewMode, setViewMode] = useState('grid');

    const getPeopleWithLocation = useMemo(() => {
      const today = new Date().toISOString().split('T')[0];
      
      return people.map(person => {
        const currentAssignment = siteAssignments.find(a => 
          a.person_id === person.id &&
          a.start_date <= today &&
          a.end_date >= today &&
          a.status === 'on_site'
        );

        return {
          ...person,
          currentLocation: currentAssignment ? {
            location: currentAssignment.location,
            project: projects.find(p => p.id === currentAssignment.project_id),
            role: currentAssignment.role_on_site,
            until: currentAssignment.end_date
          } : null
        };
      });
    }, [people, siteAssignments, projects]);

    const filteredPeople = getPeopleWithLocation.filter(person => {
      const matchesSearch = person.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            person.email?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesDepartment = filterDepartment === 'all' || person.department === filterDepartment;
      return matchesSearch && matchesDepartment;
    });

    const departments = [...new Set(people.map(p => p.department).filter(Boolean))];

    const getStatusColor = (person) => {
      if (person.currentLocation) return 'bg-green-100 text-green-700';
      if (person.location_status === 'on_site') return 'bg-green-100 text-green-700';
      if (person.location_status === 'remote') return 'bg-purple-100 text-purple-700';
      return 'bg-blue-100 text-blue-700';
    };

    const getStatusText = (person) => {
      if (person.currentLocation) return `On Site: ${person.currentLocation.location}`;
      if (person.location_status === 'on_site' && person.current_location) return `On Site: ${person.current_location}`;
      if (person.location_status === 'remote') return 'Working Remote';
      return 'At HQ';
    };

    return (
      <div className="space-y-6">
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-center space-x-4 flex-1">
              <div className="relative flex-1 max-w-md">
                <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 w-full border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <select 
                value={filterDepartment}
                onChange={(e) => setFilterDepartment(e.target.value)}
                className="px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Departments</option>
                {departments.map(dept => (
                  <option key={dept} value={dept}>{dept}</option>
                ))}
              </select>
            </div>

            <div className="flex items-center space-x-2">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-lg ${viewMode === 'grid' ? 'bg-blue-100 text-blue-600' : 'text-slate-400'}`}
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <rect x="3" y="3" width="7" height="7" strokeWidth="2"/>
                  <rect x="14" y="3" width="7" height="7" strokeWidth="2"/>
                  <rect x="3" y="14" width="7" height="7" strokeWidth="2"/>
                  <rect x="14" y="14" width="7" height="7" strokeWidth="2"/>
                </svg>
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-lg ${viewMode === 'list' ? 'bg-blue-100 text-blue-600' : 'text-slate-400'}`}
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <line x1="3" y1="6" x2="21" y2="6" strokeWidth="2"/>
                  <line x1="3" y1="12" x2="21" y2="12" strokeWidth="2"/>
                  <line x1="3" y1="18" x2="21" y2="18" strokeWidth="2"/>
                </svg>
              </button>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-4 gap-4 mt-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-slate-900">{people.length}</div>
              <div className="text-sm text-slate-600">Total Staff</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {people.filter(p => p.location_status === 'on_site' || getPeopleWithLocation.find(pw => pw.id === p.id)?.currentLocation).length}
              </div>
              <div className="text-sm text-slate-600">On Site</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">
                {people.filter(p => p.location_status === 'remote').length}
              </div>
              <div className="text-sm text-slate-600">Remote</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">
                {people.filter(p => p.location_status === 'hq' || (!p.location_status && !getPeopleWithLocation.find(pw => pw.id === p.id)?.currentLocation)).length}
              </div>
              <div className="text-sm text-slate-600">At HQ</div>
            </div>
          </div>
        </div>

        {/* People Grid/List */}
        {viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredPeople.map(person => (
              <div key={person.id} className="bg-white rounded-xl p-6 shadow-sm border border-slate-200 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-16 h-16 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-full flex items-center justify-center text-white font-bold text-xl">
                    {person.name?.split(' ').map(n => n[0]).join('') || '??'}
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(person)}`}>
                    {person.currentLocation ? 'On Site' : 
                     person.location_status === 'on_site' ? 'On Site' :
                     person.location_status === 'remote' ? 'Remote' : 'HQ'}
                  </span>
                </div>
                
                <h3 className="font-semibold text-slate-900 mb-1">{person.name}</h3>
                <p className="text-sm text-slate-600 mb-3">{person.role || 'Employee'}</p>
                
                <div className="space-y-2 text-sm">
                  <div className="flex items-center text-slate-600">
                    <Mail className="w-4 h-4 mr-2 text-slate-400" />
                    <span className="truncate">{person.email}</span>
                  </div>
                  {person.phone && (
                    <div className="flex items-center text-slate-600">
                      <Phone className="w-4 h-4 mr-2 text-slate-400" />
                      {person.phone}
                    </div>
                  )}
                  {person.department && (
                    <div className="flex items-center text-slate-600">
                      <Building className="w-4 h-4 mr-2 text-slate-400" />
                      {person.department}
                    </div>
                  )}
                  {(person.currentLocation || (person.location_status === 'on_site' && person.current_location)) && (
                    <div className="flex items-start text-slate-600">
                      <MapPin className="w-4 h-4 mr-2 text-slate-400 mt-0.5" />
                      <div>
                        <div className="font-medium">
                          {person.currentLocation?.location || person.current_location}
                        </div>
                        {person.currentLocation?.project && (
                          <div className="text-xs text-slate-500">
                            {person.currentLocation.project.project_id}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (<div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left p-4 font-semibold text-slate-900">Name</th>
                  <th className="text-left p-4 font-semibold text-slate-900">Role</th>
                  <th className="text-left p-4 font-semibold text-slate-900">Department</th>
                  <th className="text-left p-4 font-semibold text-slate-900">Location</th>
                  <th className="text-left p-4 font-semibold text-slate-900">Contact</th>
                </tr>
              </thead>
              <tbody>
                {filteredPeople.map(person => (
                  <tr key={person.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="p-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                          {person.name?.split(' ').map(n => n[0]).join('') || '??'}
                        </div>
                        <div>
                          <div className="font-medium text-slate-900">{person.name}</div>
                          <div className="text-sm text-slate-500">{person.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="p-4 text-slate-600">{person.role || 'Employee'}</td>
                    <td className="p-4 text-slate-600">{person.department || '-'}</td>
                    <td className="p-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(person)}`}>
                        {getStatusText(person)}
                      </span>
                    </td>
                    <td className="p-4 text-slate-600">{person.phone || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  // ============= PROJECTS VIEW COMPONENT =============
  const ProjectsView = () => {
    const [filter, setFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');

    const filteredProjects = projects.filter(project => {
      const matchesFilter = filter === 'all' || project.status === filter;
      const matchesSearch = 
        project.project_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        project.client?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        project.location?.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesFilter && matchesSearch;
    });

    const getStatusColor = (status) => {
      switch(status) {
        case 'active': return 'bg-green-100 text-green-700';
        case 'completed': return 'bg-blue-100 text-blue-700';
        case 'on_hold': return 'bg-yellow-100 text-yellow-700';
        default: return 'bg-gray-100 text-gray-700';
      }
    };

    const getProjectProgress = (project) => {
      if (!project.start_date || !project.end_date) return 0;
      const start = new Date(project.start_date);
      const end = new Date(project.end_date);
      const now = new Date();
      
      if (now < start) return 0;
      if (now > end) return 100;
      
      const total = end - start;
      const elapsed = now - start;
      return Math.round((elapsed / total) * 100);
    };

    return (
      <div className="space-y-6">
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-slate-900">Project Management</h2>
            <div className="flex items-center space-x-3">
              <div className="relative">
                <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search projects..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <button className="px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors flex items-center">
                <Plus className="w-4 h-4 mr-2" />
                New Project
              </button>
            </div>
          </div>

          <div className="flex space-x-2 mb-6">
            {['all', 'active', 'completed', 'on_hold'].map(status => (
              <button
                key={status}
                onClick={() => setFilter(status)}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  filter === status 
                    ? 'bg-blue-100 text-blue-700' 
                    : 'text-slate-600 hover:bg-slate-50'
                }`}
              >
                {status === 'all' ? 'All' : status.replace('_', ' ').charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
                <span className="ml-2 text-sm">
                  ({status === 'all' ? projects.length : projects.filter(p => p.status === status).length})
                </span>
              </button>
            ))}
          </div>

          <div className="grid gap-4">
            {filteredProjects.map(project => (
              <div key={project.id} className="border border-slate-200 rounded-xl p-6 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-lg font-semibold text-slate-900">{project.project_id}</h3>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(project.status)}`}>
                        {project.status?.replace('_', ' ')}
                      </span>
                    </div>
                    <p className="text-slate-600 mb-1">{project.client}</p>
                    <p className="text-sm text-slate-500 flex items-center">
                      <MapPin className="w-4 h-4 mr-1" />
                      {project.location || 'Location TBD'}
                    </p>
                  </div>
                  <div className="flex space-x-2">
                    <button className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">
                      <Edit className="w-4 h-4" />
                    </button>
                    <button className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">
                      <MoreVertical className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                
                {project.description && (
                  <p className="text-sm text-slate-600 mb-4">{project.description}</p>
                )}

                {/* Progress Bar */}
                {project.status === 'active' && project.start_date && project.end_date && (
                  <div className="mb-4">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-slate-600">Progress</span>
                      <span className="font-medium">{getProjectProgress(project)}%</span>
                    </div>
                    <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full"
                        style={{ width: `${getProjectProgress(project)}%` }}
                      />
                    </div>
                  </div>
                )}
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-slate-500">PM:</span>
                    <p className="font-medium">{project.project_manager?.name || 'Unassigned'}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Lead Engineer:</span>
                    <p className="font-medium">{project.lead_engineer?.name || 'Unassigned'}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Start Date:</span>
                    <p className="font-medium">{project.start_date ? formatDate(project.start_date) : 'TBD'}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">End Date:</span>
                    <p className="font-medium">{project.end_date ? formatDate(project.end_date) : 'TBD'}</p>
                  </div>
                </div>

                {/* Quick Stats */}
                <div className="flex items-center space-x-6 mt-4 pt-4 border-t border-slate-200 text-sm">
                  <div className="flex items-center text-slate-600">
                    <Users className="w-4 h-4 mr-1" />
                    {siteAssignments.filter(a => a.project_id === project.id).length} assigned
                  </div>
                  <div className="flex items-center text-slate-600">
                    <Receipt className="w-4 h-4 mr-1" />
                    {expenses.filter(e => e.project_id === project.id).length} expenses
                  </div>
                  <div className="flex items-center text-slate-600">
                    <Plane className="w-4 h-4 mr-1" />
                    {travelRequests.filter(t => t.project_id === project.id).length} travels
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // ============= CALENDAR VIEW COMPONENT =============
  const CalendarView = () => {
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState(null);
    const [viewType, setViewType] = useState('month');

    const getDaysInMonth = (date) => {
      const year = date.getFullYear();
      const month = date.getMonth();
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);
      const daysInMonth = lastDay.getDate();
      const startingDayOfWeek = firstDay.getDay();
      
      const days = [];
      
      for (let i = 0; i < startingDayOfWeek; i++) {
        days.push(null);
      }
      
      for (let i = 1; i <= daysInMonth; i++) {
        days.push(new Date(year, month, i));
      }
      
      return days;
    };

    const getEventsForDate = (date) => {
      if (!date) return { assignments: [], leaves: [], travel: [] };
      
      const dateStr = date.toISOString().split('T')[0];
      
      const assignments = siteAssignments.filter(a => 
        a.start_date <= dateStr && a.end_date >= dateStr
      );
      
      const leaves = leaveRequests.filter(l => 
        l.start_date <= dateStr && l.end_date >= dateStr && l.status === 'approved'
      );

      const travel = travelRequests.filter(t =>
        (t.departure_date === dateStr || t.return_date === dateStr) && t.status === 'approved'
      );
      
      return { assignments, leaves, travel };
    };

    const days = getDaysInMonth(currentMonth);
    const monthName = currentMonth.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });

    const navigateMonth = (direction) => {
      setCurrentMonth(prev => {
        const newMonth = new Date(prev);
        newMonth.setMonth(prev.getMonth() + direction);
        return newMonth;
      });
    };

    const today = new Date().toISOString().split('T')[0];

    // Agenda view data
    const upcomingEvents = useMemo(() => {
      const events = [];
      const startDate = new Date();
      const endDate = new Date();
      endDate.setDate(endDate.getDate() + 30);

      siteAssignments.forEach(assignment => {
        if (new Date(assignment.start_date) >= startDate && new Date(assignment.start_date) <= endDate) {
          events.push({
            type: 'assignment',
            date: assignment.start_date,
            title: `${assignment.person?.name} → ${assignment.location}`,
            details: assignment.role_on_site,
            data: assignment
          });
        }
      });

      leaveRequests.forEach(leave => {
        if (leave.status === 'approved' && new Date(leave.start_date) >= startDate && new Date(leave.start_date) <= endDate) {
          events.push({
            type: 'leave',
            date: leave.start_date,
            title: `${leave.employee?.name} - Leave`,
            details: `${leave.type} (${leave.days} days)`,
            data: leave
          });
        }
      });

      travelRequests.forEach(travel => {
        if (travel.status === 'approved' && new Date(travel.departure_date) >= startDate && new Date(travel.departure_date) <= endDate) {
          events.push({
            type: 'travel',
            date: travel.departure_date,
            title: `${travel.requester?.name} → ${travel.destination}`,
            details: travel.purpose,
            data: travel
          });
        }
      });

      return events.sort((a, b) => new Date(a.date) - new Date(b.date));
    }, [siteAssignments, leaveRequests, travelRequests]);

    return (
      <div className="space-y-6">
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigateMonth(-1)}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <h2 className="text-2xl font-bold text-slate-900">{monthName}</h2>
              <button
                onClick={() => navigateMonth(1)}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
            
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setCurrentMonth(new Date())}
                className="px-4 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              >
                Today
              </button>
              <div className="flex space-x-1 bg-slate-100 rounded-lg p-1">
                {['month', 'agenda'].map(type => (
                  <button
                    key={type}
                    onClick={() => setViewType(type)}
                    className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                      viewType === type 
                        ? 'bg-white text-slate-900 shadow-sm' 
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {viewType === 'month' ? (
            <div className="grid grid-cols-7 gap-px bg-slate-200 rounded-lg overflow-hidden">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="bg-slate-50 p-3 text-center text-sm font-semibold text-slate-700">
                  {day}
                </div>
              ))}
              {days.map((day, index) => {
                const events = day ? getEventsForDate(day) : { assignments: [], leaves: [], travel: [] };
                const isToday = day && day.toISOString().split('T')[0] === today;
                const hasEvents = events.assignments.length > 0 || events.leaves.length > 0 || events.travel.length > 0;
                
                return (
                  <div
                    key={index}
                    onClick={() => day && setSelectedDate(day)}
                    className={`
                      bg-white min-h-[100px] p-2 cursor-pointer hover:bg-slate-50 transition-colors
                      ${isToday ? 'ring-2 ring-blue-500' : ''}
                      ${selectedDate && day && selectedDate.toISOString() === day.toISOString() ? 'bg-blue-50' : ''}
                    `}
                  >
                    {day && (
                      <>
                        <div className={`text-sm font-medium mb-1 ${isToday ? 'text-blue-600' : 'text-slate-900'}`}>
                          {day.getDate()}
                        </div>
                        {hasEvents && (
                          <div className="space-y-1">
                            {events.assignments.slice(0, 2).map(a => (
                              <div key={a.id} className="text-xs bg-green-100 text-green-700 px-1 py-0.5 rounded truncate">
                                {a.person?.name?.split(' ')[0]} @ {a.location}
                              </div>
                            ))}
                            {events.leaves.slice(0, 1).map(l => (
                              <div key={l.id} className="text-xs bg-yellow-100 text-yellow-700 px-1 py-0.5 rounded truncate">
                                {l.employee?.name?.split(' ')[0]} - Leave
                              </div>
                            ))}
                            {events.travel.slice(0, 1).map(t => (
                              <div key={t.id} className="text-xs bg-blue-100 text-blue-700 px-1 py-0.5 rounded truncate">
                                ✈️ {t.destination}
                              </div>
                            ))}
                            {(events.assignments.length + events.leaves.length + events.travel.length > 3) && (
                              <div className="text-xs text-slate-500">
                                +{events.assignments.length + events.leaves.length + events.travel.length - 3} more
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="space-y-4">
              <h3 className="font-semibold text-slate-900">Next 30 Days</h3>
              <div className="space-y-3 max-h-[600px] overflow-y-auto">
                {upcomingEvents.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">No upcoming events</p>
                ) : (
                  upcomingEvents.map((event, idx) => (
                    <div key={idx} className={`flex items-center justify-between p-3 rounded-lg ${
                      event.type === 'assignment' ? 'bg-green-50' :
                      event.type === 'leave' ? 'bg-yellow-50' :
                      'bg-blue-50'
                    }`}>
                      <div className="flex items-center space-x-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          event.type === 'assignment' ? 'bg-green-100 text-green-600' :
                          event.type === 'leave' ? 'bg-yellow-100 text-yellow-600' :
                          'bg-blue-100 text-blue-600'
                        }`}>
                          {event.type === 'assignment' ? <MapPin className="w-5 h-5" /> :
                           event.type === 'leave' ? <Sun className="w-5 h-5" /> :
                           <Plane className="w-5 h-5" />}
                        </div>
                        <div>
                          <div className="font-medium text-slate-900">{event.title}</div>
                          <div className="text-sm text-slate-600">{event.details}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-medium text-slate-900">
                          {formatDate(event.date)}
                        </div>
                        <div className="text-xs text-slate-500">
                          {new Date(event.date).toLocaleDateString('en-GB', { weekday: 'short' })}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        {selectedDate && viewType === 'month' && (
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">
              Events for {selectedDate.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
            </h3>
            {(() => {
              const events = getEventsForDate(selectedDate);
              if (events.assignments.length === 0 && events.leaves.length === 0 && events.travel.length === 0) {
                return <p className="text-slate-500">No events scheduled for this date</p>;
              }
              return (
                <div className="space-y-3">
                  {events.assignments.map(a => (
                    <div key={a.id} className="p-3 bg-green-50 rounded-lg">
                      <div className="font-medium text-slate-900">{a.person?.name} - Site Assignment</div>
                      <div className="text-sm text-slate-600">{a.location} • {a.role_on_site}</div>
                      {a.project && <div className="text-xs text-slate-500 mt-1">Project: {a.project.project_id}</div>}
                    </div>
                  ))}
                  {events.leaves.map(l => (
                    <div key={l.id} className="p-3 bg-yellow-50 rounded-lg">
                      <div className="font-medium text-slate-900">{l.employee?.name} - On Leave</div>
                      <div className="text-sm text-slate-600">{l.type} • {l.days} days total</div>
                    </div>
                  ))}
                  {events.travel.map(t => (
                    <div key={t.id} className="p-3 bg-blue-50 rounded-lg">
                      <div className="font-medium text-slate-900">Travel: {t.destination}</div>
                      <div className="text-sm text-slate-600">{t.requester?.name} • {t.purpose}</div>
                      {t.project && <div className="text-xs text-slate-500 mt-1">Project: {t.project.project_id}</div>}
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        )}
      </div>
    );
  };

  // Navigation sections
  const navigationSections = [
    { id: 'home', name: 'Home', icon: <Home className="w-5 h-5" /> },
    { id: 'projects', name: 'Projects', icon: <FolderOpen className="w-5 h-5" /> },
    { id: 'operations', name: 'Operations', icon: <Activity className="w-5 h-5" /> },
    { id: 'people', name: 'People', icon: <Users className="w-5 h-5" /> },
    { id: 'requests', name: 'My Requests', icon: <FileText className="w-5 h-5" /> },
    { id: 'calendar', name: 'Calendar', icon: <CalendarDays className="w-5 h-5" /> },
    { id: 'documents', name: 'Documents', icon: <BookOpen className="w-5 h-5" /> },
    { id: 'reports', name: 'Reports', icon: <BarChart3 className="w-5 h-5" /> },
    { id: 'company', name: 'Company News', icon: <MessageSquare className="w-5 h-5" /> },
    { id: 'settings', name: 'Settings', icon: <Settings className="w-5 h-5" /> }
  ];

  const renderContent = () => {
    switch (currentView) {
      case 'home': 
        return <HomePage />;
      case 'people':
        return <PeopleDirectory />;
      case 'requests':
        return <MyRequestsDashboard />;
      case 'operations':
        return <OperationsDashboard />;
      case 'documents':
        return <DocumentsLibrary />;
      case 'reports':
        return <ReportsCenter />;
      case 'company':
        return <CompanyNews />;
      case 'settings':
        return <SettingsPage />;
      case 'projects':
        return <ProjectsView />;
      case 'calendar':
        return <CalendarView />;
      default: 
        const section = navigationSections.find(s => s.id === currentView);
        return (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-slate-100 rounded-xl flex items-center justify-center mx-auto mb-4">
              {section?.icon}
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">{section?.name}</h2>
            <p className="text-slate-600">This section is coming soon...</p>
          </div>
        );
    }
  };

  // Loading screen
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-2xl flex items-center justify-center mx-auto mb-6 animate-pulse">
            <Factory className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-xl font-bold text-slate-900 mb-2">Galo Industries</h2>
          <p className="text-slate-600">Loading your workspace...</p>
        </div>
      </div>
    );
  }

  // Show auth form if not logged in
  if (!user) {
    return <AuthForm />;
  }

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Sidebar */}
      <div className={`${sidebarOpen ? 'w-72' : 'w-20'} bg-white border-r border-slate-200 transition-all duration-300 flex flex-col shadow-sm`}>
        <div className="p-6 border-b border-slate-200">
          <div className="flex items-center justify-between">
            {sidebarOpen && (
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-2xl flex items-center justify-center shadow-lg">
                  <Factory className="w-7 h-7 text-white" />
                </div>
                <div>
                  <div className="text-slate-900 font-bold text-xl">Galo Industries</div>
                  <div className="text-blue-600 text-sm font-medium">Intranet Portal</div>
                </div>
              </div>
            )}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl transition-colors"
            >
              {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {navigationSections.map((section) => (
            <button
              key={section.id}
              onClick={() => setCurrentView(section.id)}
              className={`w-full flex items-center space-x-3 p-3 rounded-xl transition-all duration-200 text-left ${
                currentView === section.id
                  ? 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white shadow-lg'
                  : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
              }`}
            >
              <span className="flex-shrink-0">{section.icon}</span>
              {sidebarOpen && <span className="font-medium truncate">{section.name}</span>}
            </button>
          ))}
        </nav>

        <div className="p-4 border-t border-slate-200">
          {sidebarOpen ? (
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center text-white font-bold text-sm">
                  {userProfile?.name?.split(' ').map(n => n[0]).join('') || user?.email?.[0].toUpperCase()}
                </div>
                <div className="flex-1">
                  <div className="text-slate-900 font-medium text-sm">
                    {userProfile?.name || user?.email?.split('@')[0]}
                  </div>
                  <div className="text-slate-500 text-xs">{userProfile?.role || 'Employee'}</div>
                </div>
              </div>
              <button
                onClick={signOut}
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                title="Sign Out"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div className="flex flex-col items-center space-y-2">
              <div className="w-10 h-10 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center text-white font-bold text-sm">
                {userProfile?.name?.split(' ').map(n => n[0]).join('') || user?.email?.[0].toUpperCase()}
              </div>
              <button
                onClick={signOut}
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                title="Sign Out"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        <header className="bg-white border-b border-slate-200 px-8 py-6">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-slate-900">
              {navigationSections.find(s => s.id === currentView)?.name || 'Home'}
            </h1>
            
            <div className="flex items-center space-x-4">
              <div className="relative hidden md:block">
                <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search..."
                  className="pl-10 pr-4 py-2 w-64 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-slate-50"
                />
              </div>
              <button className="relative p-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-xl transition-colors">
                <Bell className="w-6 h-6" />
                {news.length > 0 && (
                  <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                )}
              </button>
            </div>
          </div>
        </header>

        {(error || success) && (
          <div className={`px-8 py-3 border-b ${error ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
            <div className="flex items-center justify-between">
              <div className={`text-sm ${error ? 'text-red-800' : 'text-green-800'}`}>
                {error || success}
              </div>
              <button 
                onClick={() => {
                  setError(null);
                  setSuccess(null);
                }}
                className={`p-1 ${error ? 'text-red-600 hover:text-red-800' : 'text-green-600 hover:text-green-800'}`}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        <main className="flex-1 p-8 overflow-auto">
          {renderContent()}
        </main>
      </div>

      {/* Forms & Modals */}
      {showForm === 'travel' && <TravelRequestForm />}
      {showForm === 'expense' && <ExpenseForm />}
      {showForm === 'leave' && <LeaveRequestForm />}
      {showStatusModal && <StatusUpdateModal />}
    </div>
  );
};

export default GaloIntranet;